import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ShieldCheck, Send } from "lucide-react";

/**
 * OTP verification over SMS (Fast2SMS).
 *
 * Used when staff add a new customer at the counter, and on customer signup.
 * Calls onVerified() once the number is confirmed.
 *
 * While Fast2SMS isn't configured the backend runs in dev mode and returns the
 * code, which is shown here so the flow can be tested before the client's SMS
 * account is live.
 */
export default function OtpVerify({ phone, purpose = "verify", onVerified, compact = false }) {
  const [sent, setSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [verified, setVerified] = useState(false);
  const [devOtp, setDevOtp] = useState("");
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  // Changing the number invalidates any verification already done
  useEffect(() => { setSent(false); setVerified(false); setOtp(""); setDevOtp(""); }, [phone]);

  const send = async () => {
    if (!/^[6-9]\d{9}$/.test(phone || "")) { toast.error("Enter a valid 10-digit mobile number first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/api/otp/send", { phone, purpose });
      setSent(true);
      setCooldown(60);
      if (data?.dev_mode && data?.otp) {
        setDevOtp(data.otp);
        toast.info("SMS not configured yet — code shown on screen for testing");
      } else {
        toast.success(`OTP sent to ${phone}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the OTP");
    } finally { setBusy(false); }
  };

  const verify = async () => {
    setBusy(true);
    try {
      await api.post("/api/otp/verify", { phone, otp, purpose });
      setVerified(true);
      toast.success("Number verified");
      onVerified?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not verify the OTP");
    } finally { setBusy(false); }
  };

  if (verified) {
    return (
      <div className="flex items-center gap-2 text-sm text-green-700" data-testid="otp-verified">
        <ShieldCheck size={16}/> {phone} verified
      </div>
    );
  }

  return (
    <div className={compact ? "" : "bg-pearl/60 border border-line p-3"} data-testid="otp-box">
      {!sent ? (
        <button
          type="button"
          onClick={send}
          disabled={busy}
          className="btn-outline text-xs inline-flex items-center gap-1"
          data-testid="otp-send"
        >
          <Send size={12}/> {busy ? "Sending…" : "Send OTP"}
        </button>
      ) : (
        <div className="space-y-2">
          <div className="text-[11px] text-ash">
            Enter the 6-digit code sent to <b className="text-maroon">{phone}</b>.
            {devOtp && <span className="ml-1">Test code: <b className="text-maroon font-mono">{devOtp}</b></span>}
          </div>
          <div className="flex gap-2">
            <input
              className="border border-line px-3 py-2 bg-white text-sm w-32 tracking-[0.3em] font-mono"
              placeholder="------"
              maxLength={6}
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              data-testid="otp-input"
            />
            <button type="button" onClick={verify} disabled={busy || otp.length !== 6}
              className="btn-maroon text-xs disabled:opacity-50" data-testid="otp-verify">
              {busy ? "Verifying…" : "Verify"}
            </button>
            <button type="button" onClick={send} disabled={busy || cooldown > 0}
              className="text-xs text-maroon underline disabled:opacity-40 disabled:no-underline" data-testid="otp-resend">
              {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
